import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, PinInput, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import * as authApi from '@/features/auth/api';
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';
import { ensureFingerprint } from '@/shared/lib/fingerprint';
import { env } from '@/shared/lib/env';
import { useAuth } from '@/app/providers/AuthProvider';

interface OtpSearch {
  email?: string;
}

export const Route = createFileRoute('/_auth/register-otp')({
  validateSearch: (s: Record<string, unknown>): OtpSearch => ({
    email: typeof s.email === 'string' ? s.email : undefined,
  }),
  component: RegisterOtpPage,
});

function RegisterOtpPage() {
  const navigate = useNavigate();
  const { email } = Route.useSearch();
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const auth = useAuth();

  const onSubmit = async () => {
    if (!email || otp.length < 6) {
      notifications.show({ color: 'yellow', message: 'Inserisci il codice di 6 cifre' });
      return;
    }
    setBusy(true);
    try {
      const fingerprint = await ensureFingerprint();
      await authApi.registerWithOtp({ Email: email, Otp: otp, FingerPrint: fingerprint, UserLang: env.VITE_DEFAULT_LANG });
      // BE returns auth bundle on success: refresh provider to pick up the new tokens via cookie/refresh.
      await auth.refresh();
      notifications.show({ color: 'green', message: 'Account confermato' });
      void navigate({ to: '/' });
    } catch (err) {
      const msg = err instanceof JsonApiHttpError ? (err.errors[0]?.detail ?? 'Codice non valido') : err instanceof Error ? err.message : 'Errore';
      notifications.show({ color: 'red', title: 'Verifica OTP fallita', message: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack align="center">
      <Title order={2}>Inserisci il codice OTP</Title>
      <Text c="dimmed" size="sm" ta="center">
        Abbiamo inviato un codice di 6 cifre a <strong>{email ?? 'la tua email'}</strong>
      </Text>
      <PinInput length={6} type="number" oneTimeCode value={otp} onChange={setOtp} size="lg" />
      <Button onClick={onSubmit} loading={busy} fullWidth size="md" disabled={otp.length < 6}>
        Conferma
      </Button>
    </Stack>
  );
}
